// Feed Analysis modal — what has gone into the shed against what the breed
// standard says the birds need.
//
// The vocabulary here is deliberate and should not drift: feed is GIVEN,
// DELIVERED, IN HAND, TO ORDER. It is never "eaten", "intake" or "consumed".
// chickweightfeed records deliveries (see docs/dailyOperations.md), so a flock
// normally runs 20-60% "above standard" for its first three weeks purely
// because feed sits in the bin before the birds eat it. Every number on this
// screen is framed to make that readable rather than alarming, and the API
// suppresses the spec's wastage/conversion diagnosis for the same reason.
//
// Unlike ForecastDetailModal, this one fetches its own data when opened: the
// per-day series is too heavy to ride along on the polled dashboard payload.

import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import MaterialIcon from 'material-icons-react';

import { ProjectionMiniChart, InsightCards, hexToRgba } from './chartPrimitives';
import { getFeedAnalysis } from '../utils/axiosinstace';

// Feed's own categorical identity. Teal rather than the dashboard navy so the
// flock's feed line reads as its own subject, and deliberately NOT one of the
// reserved status colours (#22C55E ahead / #3B82F6 on-track / #F59E0B behind) --
// colouring "more feed than standard" green or red would assert exactly the
// good/bad judgement this module refuses to make on delivered feed.
//
// #0D9488 vs #6B6BB8 validated as a categorical pair: deutan dE 11.1, tritan
// 8.9, normal-vision 17.1, both >= 3:1 on the surface. All six checks pass.
const FEED = '#0D9488';        // feed given -- the flock's own record
const FEED_LIGHT = '#2DD4BF';  // top of the bar gradient, same ramp
// The standard requirement. Same blue as the corridor band in the shared
// chart, so "the standard" is one colour wherever it appears. Validated as a
// solid line against the teal bars: deutan 20.7, normal-vision 22.6.
const STD = '#2563EB';
const GREY = '#9CA3AF';        // absence marker, deliberately recessive
const AMBER = '#F59E0B';       // reserved status: a recorded zero, or running short
// A TEXT token, not a series colour. Legend and axis text never wear the data
// hue -- identity comes from the coloured swatch beside the label.
const INK = '#292970';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

export default function FeedAnalysisModal({ show, onClose, farmID, sexing }) {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'charts']);

  const [phase, setPhase] = useState('idle');     // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [entered, setEntered] = useState(false);
  const [tab, setTab] = useState('cumulative');

  // Depend on PRIMITIVES only. Dashboard.js polls every five minutes and hands
  // back fresh object identities each time; depending on one of those objects
  // would refetch and remount the chart every poll while the modal is open.
  useEffect(() => {
    if (!show || !farmID) return undefined;
    const ac = new AbortController();
    let alive = true;
    setPhase('loading');
    setData(null);
    getFeedAnalysis(farmID, ac.signal)
      .then((res) => {
        if (!alive) return;
        if (res && res.status) { setData(res); setPhase('ready'); }
        else { setPhase('error'); }
      })
      .catch((e) => {
        if (!alive) return;
        // A modal closed mid-flight is not an error worth showing.
        if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError')) return;
        setPhase('error');
      });
    // abort kills the request; `alive` closes the race where a response lands
    // in the same tick as teardown.
    return () => { alive = false; ac.abort(); };
  }, [show, farmID, reloadKey]);

  useEffect(() => { if (show) setTab('cumulative'); }, [show]);

  const i18nNum = useMemo(() => (v, dp) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    try {
      return new Intl.NumberFormat(i18n.language || 'en', {
        minimumFractionDigits: dp, maximumFractionDigits: dp
      }).format(n);
    } catch (e) { return n.toFixed(dp); }
  }, [i18n.language]);

  // Farmers order feed in tonnes and top up in kilos. Switch at a tonne.
  const mass = useMemo(() => (kg) => {
    const n = Number(kg);
    if (!Number.isFinite(n)) return '—';
    const a = Math.abs(n);
    return a >= 1000
      ? t('dashboard:fcr.insights.tonnes', { value: i18nNum(n / 1000, 1) })
      : t('dashboard:fcr.insights.kilos', { value: i18nNum(Math.round(n), 0) });
  }, [t, i18nNum]);

  const body = () => {
    if (phase === 'loading' || phase === 'idle') return <FeedSkeleton />;
    if (phase === 'error') {
      return (
        <div className="noDataState">
          <MaterialIcon icon="error_outline" color={AMBER} size={56} />
          <div className="noDataText">{t('dashboard:feed.loadFailed')}</div>
          <div className="noDataHint">{t('dashboard:feed.loadFailedHint')}</div>
          <button type="button" className="modalCancelBtn noDataAction"
                  onClick={() => setReloadKey((k) => k + 1)}>
            {t('common:actions.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      );
    }
    if (!data || !data.intake || data.intake.status === 'NA') {
      return (
        <div className="noDataState">
          <MaterialIcon icon="radio_button_unchecked" color={GREY} size={56} />
          <div className="noDataText">{t('dashboard:feed.noData')}</div>
          <div className="noDataHint">{(data && data.intake && data.intake.reason) || ''}</div>
        </div>
      );
    }
    return <FeedBody data={data} tab={tab} setTab={setTab} entered={entered}
                     t={t} i18nNum={i18nNum} mass={mass} sexing={sexing} />;
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered
           dialogClassName="forecastModalWide"
           onEntered={() => setEntered(true)}
           onExited={() => setEntered(false)}>
      <Modal.Header>
        <div className="forecastModalHeader">
          <Modal.Title>{t('dashboard:feed.title')}</Modal.Title>
          <span className="forecastBreedTag">{t('dashboard:feed.givenTag')}</span>
        </div>
        <button type="button" className="modalCloseBtn" onClick={onClose}
                aria-label={t('common:actions.close')}>×</button>
      </Modal.Header>
      <Modal.Body>{body()}</Modal.Body>
      <Modal.Footer>
        <button type="button" className="modalCancelBtn" onClick={onClose}>
          {t('common:actions.close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// Holds the final layout while loading so the centred dialog does not jump.
function FeedSkeleton() {
  return (
    <div className="forecastGrid">
      <div className="forecastMain">
        <div className="kpiRow kpiRow4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="kpiCard"><div className="feedSkeleton feedSkeletonCard" /></div>
          ))}
        </div>
        <div className="forecastChartBig">
          <div className="chartMountPlaceholder" style={{ height: 380 }} />
        </div>
      </div>
      <aside className="forecastSide">
        {[0, 1, 2].map((i) => (
          <div key={i} className="insightCard insight-info">
            <div className="insightText" style={{ width: '100%' }}>
              <div className="feedSkeleton feedSkeletonLine" />
              <div className="feedSkeleton feedSkeletonLine" style={{ width: '92%' }} />
            </div>
          </div>
        ))}
      </aside>
    </div>
  );
}

function FeedBody({ data, tab, setTab, entered, t, i18nNum, mass, sexing }) {
  const intake = data.intake;
  const budget = data.budget || {};
  const chart = intake.chart || {};
  const feedDay = num(data.feed_day);
  const harvestDay = num(data.harvest_day);
  const lead = num(intake.lead_days);
  const hasBudget = budget && budget.status !== 'NA';
  const hasInHand = Array.isArray(chart.lead_days) && chart.lead_days.filter((v) => v != null).length >= 5;

  const tabs = [
    { key: 'cumulative', label: t('dashboard:feed.tabs.cumulative') },
    { key: 'daily', label: t('dashboard:feed.tabs.daily') }
  ];
  if (hasInHand) tabs.push({ key: 'inHand', label: t('dashboard:feed.tabs.inHand') });
  const impact = data.impact;
  const hasImpact = impact && impact.status === 'OK' && Array.isArray(impact.levels) && impact.levels.length >= 2;
  if (hasImpact) tabs.push({ key: 'impact', label: t('dashboard:feed.impact.tab') });
  const activeTab = tabs.some((x) => x.key === tab) ? tab : 'cumulative';

  const footer = activeTab === 'cumulative'
    ? t('dashboard:feed.chart.cumulativeFooter', { lead: intake.lead_window_days })
    : activeTab === 'daily'
      ? t('dashboard:feed.chart.dailyFooter')
      : activeTab === 'impact'
        ? t('dashboard:feed.impact.adLibNote')
        : t('dashboard:feed.chart.inHandFooter');

  return (
    <>
      <div className="forecastSubtitle">
        {t('common:time.day')}{' '}
        {t('dashboard:labels.dayOfHarvest', {
          day: data.day, total: harvestDay,
          left: Math.max((harvestDay || 0) - (data.day || 0), 0)
        })}
        {data.standard && data.standard.breed && (
          <span className="forecastBreedTag">
            {data.standard.breed}
            {data.standard.year ? ` · ${data.standard.year}` : ''}
          </span>
        )}
      </div>

      <div className="forecastCapNote">
        <MaterialIcon icon="info" color={STD} size={14} />
        <span>{t('dashboard:feed.givenNote')}</span>
      </div>
      {sexing === 'yes' && (
        <div className="forecastCapNote">
          <MaterialIcon icon="info" color={STD} size={14} />
          <span>{t('dashboard:feed.sexedNote')}</span>
        </div>
      )}

      <div className="forecastGrid">
        <div className="forecastMain">
          <div className="kpiRow kpiRow4">
            <div className="kpiCard">
              <div className="kpiLabel">{t('dashboard:feed.kpi.givenSoFar')}</div>
              <div className="kpiValue">{mass(intake.cum_given_kg)}</div>
              <div className="kpiSub">
                {t('dashboard:feed.kpi.perBird', { value: i18nNum((intake.cum_given_per_bird_g || 0) / 1000, 2) })}
              </div>
            </div>
            <div className="kpiCard">
              <div className="kpiLabel">{t('dashboard:feed.kpi.neededByNow')}</div>
              <div className="kpiValue">{mass(intake.standard_required_kg)}</div>
              <div className="kpiSub">
                {t('dashboard:feed.kpi.perBird', { value: i18nNum((intake.standard_required_per_bird_g || 0) / 1000, 2) })}
              </div>
            </div>
            <div className="kpiCard">
              <div className="kpiLabel">{t('dashboard:feed.kpi.inHand')}</div>
              <div className="kpiValue" style={{ color: lead != null && lead < 0 ? AMBER : undefined }}>
                {hasBudget ? mass(Math.abs(budget.on_hand_kg)) : '—'}
              </div>
              <div className="kpiSub">
                {lead == null ? '—'
                  : lead < 0
                    ? t('dashboard:feed.kpi.short')
                    : t('dashboard:feed.kpi.days', { value: i18nNum(lead, 1) })}
              </div>
            </div>
            <div className="kpiCard kpiCardAccent">
              <div className="kpiLabel">{t('dashboard:feed.kpi.stillToOrder')}</div>
              <div className="kpiValue kpiValueAccent">
                {hasBudget ? mass(budget.still_to_order_kg) : '—'}
              </div>
              <div className="kpiFoot kpiFootMuted">
                {t('dashboard:feed.kpi.byDay', { day: harvestDay })}
              </div>
            </div>
          </div>

          {tabs.length > 1 && (
            <div className="chartTabs">
              {tabs.map((x) => (
                <button key={x.key} type="button"
                        className={`chartTab ${activeTab === x.key ? 'active' : ''}`}
                        onClick={() => setTab(x.key)}>
                  {x.label}
                </button>
              ))}
            </div>
          )}

          {/* Must stay inside .forecastChartBig — that class carries the escape
              from the global `canvas { height: 200px !important }` rule. */}
          <div className="forecastChartBig">
            {entered ? (
              <FeedChart which={activeTab} key={activeTab} data={data} t={t} i18nNum={i18nNum} />
            ) : (
              <div className="chartMountPlaceholder" style={{ height: 380 }} />
            )}
            <div className="chartFooterNote">{footer}</div>
          </div>
        </div>

        <aside className="forecastSide">
          <div className="sideHeading">{t('dashboard:fcr.whatThisMeans')}</div>
          {activeTab === 'impact'
            ? <FeedImpactInsights data={data} t={t} i18nNum={i18nNum} mass={mass} />
            : <FeedInsights data={data} t={t} i18nNum={i18nNum} mass={mass} />}
        </aside>
      </div>
    </>
  );
}

function FeedChart({ which, data, t, i18nNum }) {
  if (which === 'daily') return <FeedDailyChart data={data} t={t} i18nNum={i18nNum} />;
  if (which === 'inHand') return <FeedInHandChart data={data} t={t} i18nNum={i18nNum} />;
  if (which === 'impact') return <FeedImpactChart data={data} t={t} i18nNum={i18nNum} />;
  return <FeedCumulativeChart data={data} t={t} />;
}

// What the breed table says a shortfall costs, in saleable tonnes.
//
// ONE axis. Tonnes and FCR are different scales, so the FCR never gets a second
// y-axis -- it rides each bar as a direct label. That is also what makes the
// paradox visible in a single read: the bars fall left to right while the FCR
// labels beside them improve.
function FeedImpactChart({ data, t, i18nNum }) {
  const imp = data.impact;
  const levels = imp.levels;
  const cats = levels.map((l) => `${l.intake_pct}%`);
  const tonnes = levels.map((l) => l.flock_kg / 1000);

  // Full strength at standard, fading as intake drops -- the bar's own colour
  // carries "less", so the reader does not have to compare heights alone.
  const bars = levels.map((l, i) => ({
    value: l.flock_kg / 1000,
    itemStyle: { color: i === 0 ? FEED : hexToRgba(FEED, 0.85 - i * 0.18) }
  }));

  const option = {
    animation: false,
    grid: { top: 54, right: 28, bottom: 52, left: 68 },
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true,
      backgroundColor: 'rgba(41,41,112,0.96)', borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 12 }, padding: [8, 12],
      formatter: (params) => {
        if (!params || !params.length) return '';
        const l = levels[params[0].dataIndex];
        return `<div style="font-weight:700;margin-bottom:3px;">${t('dashboard:feed.impact.eats', { pct: l.intake_pct })}</div>`
          + `<div>${t('dashboard:feed.impact.saleable')}: <b>${i18nNum(l.flock_kg / 1000, 1)} t</b></div>`
          + (l.flock_kg_lost > 0
            ? `<div style="color:#fca5a5;font-weight:700;">${t('dashboard:feed.impact.lose', { amount: i18nNum(l.flock_kg_lost / 1000, 1) + ' t' })}</div>`
            : '')
          + `<div style="opacity:.85;">${t('dashboard:feed.impact.fcrReads', { value: i18nNum(l.fcr, 3) })}</div>`;
      }
    },
    xAxis: {
      type: 'category', data: cats,
      name: t('dashboard:feed.impact.axisIntake'),
      nameLocation: 'middle', nameGap: 30,
      nameTextStyle: { color: '#6c757d', fontSize: 11 },
      axisTick: { alignWithLabel: true },
      axisLine: { lineStyle: { color: '#c9cde0' } },
      axisLabel: { color: INK, fontWeight: 700 }
    },
    yAxis: {
      type: 'value', min: 0,
      name: t('dashboard:feed.impact.axisSaleable'),
      nameLocation: 'middle', nameGap: 48,
      nameTextStyle: { color: '#6c757d', fontSize: 11 },
      axisLabel: { color: '#6c757d' },
      splitLine: { lineStyle: { color: '#e9ebf6' } }
    },
    series: [{
      type: 'bar', data: bars,
      barWidth: '46%', barMaxWidth: 64,
      itemStyle: { color: FEED, borderRadius: [4, 4, 0, 0] },
      // The FCR at each level, labelled on the cap. This is the whole point of
      // the chart, so it is the one number allowed on every mark.
      label: {
        show: true, position: 'top', distance: 8,
        formatter: (p) => `${i18nNum(levels[p.dataIndex].fcr, 3)}`,
        color: INK, fontWeight: 800, fontSize: 12.5
      }
    }]
  };

  return (
    <ReactECharts option={option} style={{ height: 380, width: '100%' }} notMerge lazyUpdate
      onChartReady={(inst) => {
        const kick = () => { try { inst.resize(); } catch (e) { /* detached */ } };
        setTimeout(kick, 60); setTimeout(kick, 400);
      }} />
  );
}

// Cumulative feed given against a corridor of "the standard, up to N days of
// feed ahead". Reuses the corridor chart farmers already read on the FCR and
// Weight modals. The flock line simply STOPS at the last feed entry — feed
// deliveries are not forecast, so there is no dashed segment to draw.
function FeedCumulativeChart({ data, t }) {
  const c = data.intake.chart || {};
  return (
    <ProjectionMiniChart
      days={c.days || []}
      predicted={c.cum_given || []}
      ideal={c.cum_standard || []}
      bandLo={c.band_lo || null}
      bandHi={c.band_hi || null}
      today={num(data.feed_day)}
      splitAt={num(data.feed_day)}
      todayLabel={t('dashboard:labels.lastRecordedLower')}
      color={FEED}
      yInterval={0.5}
      unitSuffix=" kg"
      decimals={2}
      height={380}
      trimStart={0}
      deltaTone="neutral"
      zoneLabel={t('dashboard:feed.chart.zone')}
      zoneLabelColon={t('dashboard:feed.chart.zoneColon')}
    />
  );
}

// Feed given per bird per day, as bars, against the standard as a line.
//
// Bars, never a line: the record is a series of deliveries, and a line would
// imply a daily measurement nobody made. Days with no entry render as a grey
// column, NOT as a zero bar — a missing record is not a day without feed, and
// conflating the two is how an out-of-feed event gets hidden.
function FeedDailyChart({ data, t, i18nNum }) {
  const c = data.intake.chart || {};
  const days = c.daily_days || [];
  const given = c.daily_given || [];
  const std = c.daily_standard || [];
  const shed = c.daily_given_kg || [];
  const feedDay = num(data.feed_day);

  const stdMax = Math.max(0, ...std.filter((v) => Number.isFinite(v)));
  const axisMax = Math.max(stdMax * 1.6, ...given.map((v) => (Number.isFinite(v) ? v : 0)));

  const NAME_GIVEN = t('dashboard:feed.chart.given');
  const NAME_STD = t('dashboard:feed.chart.standard');
  const NAME_NONE = t('dashboard:feed.chart.noEntry');

  // ECharts 5 will not render an all-null series, so the "no entry" markers
  // carry the axis max rather than nulls.
  const noEntry = days.map((d, i) =>
    (given[i] == null && feedDay != null && d <= feedDay ? axisMax : null));
  // A vertical gradient on the bars, and amber for a day recorded AS zero so it
  // stays distinct from the grey "no entry" column behind it.
  const barFill = {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [{ offset: 0, color: FEED_LIGHT }, { offset: 1, color: FEED }]
  };
  const bars = days.map((d, i) => {
    const v = given[i];
    if (v == null) return null;
    return { value: v, itemStyle: { color: v === 0 ? AMBER : barFill } };
  });

  const option = {
    animation: false,
    legend: {
      top: 4, left: 'center', icon: 'roundRect', itemWidth: 20, itemHeight: 6, itemGap: 18,
      data: [NAME_GIVEN, NAME_STD, NAME_NONE],
      textStyle: { fontSize: 12.5, color: INK, fontWeight: 700 }
    },
    grid: { top: 46, right: 24, bottom: 46, left: 62 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      confine: true,
      backgroundColor: 'rgba(41,41,112,0.96)',
      borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 12 },
      padding: [8, 12],
      formatter: (params) => {
        if (!params || !params.length) return '';
        // Read from the closure by index: the no-entry series carries the axis
        // max, not a value, so params cannot be trusted for the numbers.
        const i = params[0].dataIndex;
        const g = given[i];
        const s = std[i];
        const kg = shed[i];
        const head = `<div style="font-weight:700;margin-bottom:3px;">${t('common:time.day')} ${days[i]}</div>`;
        const givenLine = g == null
          ? `<div style="opacity:.85;">${t('dashboard:feed.tooltip.noEntry')}</div>`
          : `<div>${t('dashboard:feed.tooltip.given')}: <b>${i18nNum(g, 1)} g</b></div>`
            + (kg != null ? `<div style="opacity:.7;font-size:11px;">${t('dashboard:feed.tooltip.toShed', { kg: i18nNum(kg, 0) })}</div>` : '');
        const stdLine = s == null ? ''
          : `<div style="opacity:.85;">${t('dashboard:feed.tooltip.standard')}: <b>${i18nNum(s, 1)} g</b></div>`;
        return head + givenLine + stdLine;
      }
    },
    xAxis: {
      type: 'category',
      data: days,
      name: t('common:time.day'),
      nameLocation: 'middle',
      nameGap: 26,
      axisTick: { alignWithLabel: true },
      axisLine: { lineStyle: { color: '#c9cde0' } },
      axisLabel: { interval: Math.max(0, Math.floor(days.length / 12)), color: '#6c757d' }
    },
    yAxis: {
      type: 'value',
      min: 0,
      minInterval: 25,
      name: t('charts:axis.feedPerBirdDailyG', { defaultValue: 'Feed given per bird (g/day)' }),
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: { color: '#6c757d', fontSize: 11 },
      axisLabel: { color: '#6c757d' },
      splitLine: { lineStyle: { color: '#e9ebf6' } }
    },
    series: [
      {
        name: NAME_NONE, type: 'bar', data: noEntry,
        barWidth: '100%', barGap: '-100%', z: 0, silent: true,
        itemStyle: { color: hexToRgba(GREY, 0.16) },
        emphasis: { disabled: true }
      },
      {
        // Capped thickness so a short series does not fill its slot, and a 4px
        // rounded data-end squared off at the baseline.
        name: NAME_GIVEN, type: 'bar', data: bars,
        barWidth: '58%', barMaxWidth: 24, barGap: '-100%', barMinHeight: 3, z: 3,
        // A series-level colour is what the LEGEND swatch is drawn from. The
        // per-datum gradients below still win on the bars themselves, but
        // without this the swatch falls back to an ECharts default that reads
        // almost identically to the standard line beside it.
        itemStyle: { color: FEED, borderRadius: [4, 4, 0, 0] }
      },
      {
        name: NAME_STD, type: 'line', data: std, smooth: true, showSymbol: false, z: 5,
        lineStyle: { color: STD, width: 2.5, cap: 'round', join: 'round' },
        itemStyle: { color: STD },
        areaStyle: {
          // A wash, not a block -- it separates "below standard" from "above"
          // without drawing a second hard edge.
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: hexToRgba(STD, 0.10) },
              { offset: 1, color: hexToRgba(STD, 0) }
            ]
          }
        }
      }
    ]
  };

  return (
    <ReactECharts option={option} style={{ height: 380, width: '100%' }} notMerge lazyUpdate
      onChartReady={(inst) => {
        const kick = () => { try { inst.resize(); } catch (e) { /* detached */ } };
        setTimeout(kick, 60); setTimeout(kick, 400);
      }} />
  );
}

// Days of feed in the shed. Below zero means feed has gone in more slowly than
// the birds need, which is the one alarm delivery data genuinely supports, so
// the line turns amber there.
//
// Coloured by the current value rather than by an ECharts visualMap: a
// piecewise visualMap over a series that is mostly nulls (feed reporting stops
// well before the harvest horizon) fails to map and renders nothing at all.
function FeedInHandChart({ data, t, i18nNum }) {
  const c = data.intake.chart || {};
  const days = c.days || [];
  const lead = c.lead_days || [];
  const NAME = t('dashboard:feed.chart.inHandLine');

  const finite = lead.filter((v) => v != null);
  const latest = finite.length ? finite[finite.length - 1] : null;
  const lineColor = latest != null && latest < 0 ? AMBER : FEED;
  const lo = Math.min(-0.5, ...finite);
  const hi = Math.max(1, ...finite);

  const option = {
    animation: false,
    grid: { top: 30, right: 30, bottom: 46, left: 56 },
    tooltip: {
      trigger: 'axis', confine: true,
      backgroundColor: 'rgba(41,41,112,0.96)', borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 12 }, padding: [8, 12],
      formatter: (params) => {
        if (!params || !params.length) return '';
        const i = params[0].dataIndex;
        const v = lead[i];
        if (v == null) return '';
        return `<div style="font-weight:700;margin-bottom:3px;">${t('common:time.day')} ${days[i]}</div>`
          + `<div>${t('dashboard:feed.tooltip.inHand')}: <b>${i18nNum(v, 1)}</b></div>`;
      }
    },
    xAxis: {
      type: 'category', data: days, boundaryGap: false,
      name: t('common:time.day'), nameLocation: 'middle', nameGap: 26,
      axisLine: { lineStyle: { color: '#c9cde0' } },
      axisLabel: { interval: Math.max(0, Math.floor(days.length / 8)), color: '#6c757d' }
    },
    yAxis: {
      type: 'value',
      min: Math.floor(lo), max: Math.ceil(hi), minInterval: 1,
      axisLabel: { formatter: '{value} d', color: '#6c757d' },
      splitLine: { lineStyle: { color: '#e9ebf6' } }
    },
    series: [{
      name: NAME, type: 'line', data: lead, smooth: true,
      // Show the points: with feed recorded on only a handful of days, a
      // symbol-less line can be a couple of pixels wide and read as empty.
      showSymbol: true, symbolSize: 8,
      connectNulls: false,
      lineStyle: { width: 3, color: lineColor, cap: 'round', join: 'round' },
      // 2px ring in the surface colour so points stay legible where they
      // cross the zero line or crowd together.
      itemStyle: { color: lineColor, borderColor: '#fff', borderWidth: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: hexToRgba(lineColor, 0.16) },
            { offset: 1, color: hexToRgba(lineColor, 0) }
          ]
        }
      },
      markLine: {
        silent: true, symbol: 'none',
        data: [{ yAxis: 0 }],
        lineStyle: { color: '#9aa0c0', type: 'dashed', width: 1.8 },
        // Inside the plot at the left: at the default end position the label
        // runs past the grid and gets clipped mid-word.
        label: {
          formatter: t('dashboard:feed.chart.onStandard'),
          position: 'insideStartTop',
          color: '#6c757d', fontSize: 11
        }
      }
    }]
  };

  return (
    <ReactECharts option={option} style={{ height: 380, width: '100%' }} notMerge lazyUpdate
      onChartReady={(inst) => {
        const kick = () => { try { inst.resize(); } catch (e) { /* detached */ } };
        setTimeout(kick, 60); setTimeout(kick, 400);
      }} />
  );
}

// Plain statements a farm manager can act on. Every card is derived from the
// response itself so it cannot contradict the numbers beside it, and none of
// them diagnoses a cause — on delivered-feed data that judgement is not ours
// to make.
function FeedInsights({ data, t, i18nNum, mass }) {
  const intake = data.intake || {};
  const budget = data.budget || {};
  const weight = data.weight || {};
  const cards = [];
  const lead = num(intake.lead_days);
  const feedDay = num(data.feed_day);

  if (intake.status === 'Check Data') {
    cards.push({
      key: 'check', tone: 'bad', icon: 'help_outline',
      title: t('dashboard:feed.insights.checkTitle'),
      body: t('dashboard:feed.insights.checkBody', {
        pct: `${i18nNum((intake.ratio || 0) * 100, 0)}%`
      })
    });
  }

  // When the entries themselves are suspect, do not also assert a confident
  // "N days in hand" beside the warning that says not to trust the numbers.
  const trusted = intake.status !== 'Check Data';

  if (trusted && lead != null && lead >= 0 && budget.status !== 'NA') {
    cards.push({
      key: 'lead', tone: 'info', icon: 'inventory_2',
      title: t('dashboard:feed.insights.leadTitle', { days: i18nNum(lead, 1) }),
      body: t('dashboard:feed.insights.leadBody', {
        amount: mass(Math.abs(budget.on_hand_kg)), day: feedDay
      })
    });
  } else if (trusted && lead != null && lead < 0 && budget.status !== 'NA') {
    cards.push({
      key: 'short', tone: 'bad', icon: 'warning',
      title: t('dashboard:feed.insights.shortTitle'),
      body: t('dashboard:feed.insights.shortBody', {
        amount: mass(Math.abs(budget.on_hand_kg)), day: feedDay
      })
    });
  }

  if (budget.status !== 'NA' && num(budget.still_to_order_kg) > 0 && num(budget.days_remaining) > 0) {
    cards.push({
      key: 'order', tone: 'info', icon: 'local_shipping',
      title: t('dashboard:feed.insights.orderTitle', {
        amount: mass(budget.still_to_order_kg), day: budget.to_day
      }),
      body: t('dashboard:feed.insights.orderBody', {
        daily: mass(budget.still_to_order_kg / budget.days_remaining),
        days: budget.days_remaining,
        birds: i18nNum(data.live_birds, 0)
      })
    });
  }

  // The reader that tells you whether "feed in hand" can be trusted: it assumes
  // the birds ate to standard, and the weight index is what tests that.
  if (weight && weight.status !== 'NA' && num(weight.ratio) != null) {
    cards.push({
      key: 'weight', tone: 'neutral', icon: 'monitor_weight',
      title: t('dashboard:feed.insights.weightTitle', {
        pct: `${i18nNum(weight.ratio * 100, 0)}%`
      }),
      body: t('dashboard:feed.insights.weightBody', {
        actual: i18nNum(weight.measured_kg, 3),
        ideal: i18nNum(weight.standard_kg, 3),
        day: weight.measured_on_day
      })
    });
  }

  const gaps = (intake.gaps || []).filter((g) => g.kind === 'no_entry');
  const gapDays = gaps.reduce((n, g) => n + g.days, 0);
  if (gapDays > 0) {
    const list = gaps.map((g) => (g.days === 1 ? `${g.from_day}` : `${g.from_day}–${g.to_day}`)).join(', ');
    cards.push({
      key: 'gaps', tone: 'neutral', icon: 'event_busy',
      title: t('dashboard:feed.insights.gapsTitle', { n: gapDays }),
      body: t('dashboard:feed.insights.gapsBody', { dayList: list })
    });
  }

  const lag = num(data.day) != null && feedDay != null ? data.day - feedDay : 0;
  if (lag > 0) {
    cards.push({
      key: 'lag', tone: 'neutral', icon: 'update',
      title: t('dashboard:feed.insights.lagTitle', { n: lag }),
      body: t('dashboard:feed.insights.lagBody', { day: feedDay })
    });
  }

  return <InsightCards cards={cards.slice(0, 5)} />;
}

// The rail for the "what feed is worth" tab.
//
// Everything here is read off the breed table, never off this farm's delivery
// record, so none of it claims that THIS flock's feed caused THIS flock's FCR.
// The one exception is the shortfall card, which is safe because feed that was
// never delivered genuinely cannot be eaten.
function FeedImpactInsights({ data, t, i18nNum, mass }) {
  const imp = data.impact;
  if (!imp || imp.status !== 'OK') return null;
  const budget = data.budget || {};
  const cards = [];

  // Lead with the paradox. It is the reason the whole tab exists, and the one
  // thing a farmer optimising on FCR alone would get exactly backwards.
  if (imp.fcr_moves_wrong_way) {
    const at90 = imp.levels.find((l) => l.intake_pct === 90);
    cards.push({
      key: 'paradox', tone: 'bad', icon: 'swap_vert',
      title: t('dashboard:feed.impact.paradoxTitle'),
      body: t('dashboard:feed.impact.paradoxBody')
        + (at90 ? ` (${t('dashboard:feed.impact.eats', { pct: 90 })}: −${i18nNum(at90.flock_kg_lost / 1000, 1)} t, FCR ${i18nNum(at90.fcr, 3)}.)` : '')
    });
  }

  if (imp.marginal && imp.marginal.g_gain_per_kg_feed_today != null) {
    cards.push({
      key: 'worth', tone: 'info', icon: 'scale',
      title: t('dashboard:feed.impact.worthTitle'),
      // Past the harvest day the rate is clamped to harvest, so "falling to"
      // would compare a number with itself.
      body: (imp.marginal.day >= imp.to_day
        ? t('dashboard:feed.impact.worthBodyFlat', {
            day: imp.marginal.day,
            today: i18nNum(imp.marginal.g_gain_per_kg_feed_today, 0)
          })
        : t('dashboard:feed.impact.worthBody', {
            day: imp.marginal.day,
            today: i18nNum(imp.marginal.g_gain_per_kg_feed_today, 0),
            harvest: i18nNum(imp.marginal.g_gain_per_kg_feed_at_harvest, 0),
            harvestDay: imp.to_day
          })) + (imp.marginal.early_vs_late_ratio
        ? ' ' + t('dashboard:feed.impact.worthEarly', { ratio: i18nNum(imp.marginal.early_vs_late_ratio, 2) })
        : '')
    });
  }

  // The one causal claim delivery data supports.
  if (imp.shortfall) {
    cards.push({
      key: 'short', tone: 'bad', icon: 'warning',
      title: t('dashboard:feed.impact.shortTitle', { amount: mass(imp.shortfall.short_kg) }),
      body: t('dashboard:feed.impact.shortBody', {
        perBird: i18nNum(imp.shortfall.short_per_bird_g, 0),
        day: imp.to_day,
        cost: mass(imp.shortfall.weight_cost_flock_kg)
      })
    });
  }

  // Reconcile the FCR tile. States the size of the bin component and that the
  // figure settles by harvest -- deliberately WITHOUT publishing a "corrected"
  // FCR, which would be circular: subtracting an estimate built from the
  // standard requirement just restates the weight index wearing an FCR label.
  if (budget.status !== 'NA' && num(budget.on_hand_kg) > 0) {
    cards.push({
      key: 'fcrgap', tone: 'neutral', icon: 'help_outline',
      title: t('dashboard:feed.impact.fcrGapTitle'),
      body: t('dashboard:feed.impact.fcrGapBody', { amount: mass(budget.on_hand_kg) })
    });
  }

  return <InsightCards cards={cards.slice(0, 5)} />;
}
